import { Component, Input, OnInit } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { EntityChangeRequestApiModel, EntityChangeRequestTypeEnum } from './../../../../api/journodex/model/entity-change-request-api.model';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { JournodexEntityFactory } from '../../../../Discover/Targeting/factories/journodex-entity.factory';
import { ResizeDirectionEnum } from '../../../Forms/components/TextareaInput/textarea-input.component';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ContactModel } from '../../../TargetingShared/model/contact.model';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AutocompleteChipsOptions } from '../../../Forms/components/AutocompleteChips/autocomplete-chips.component';
import { JournodexApiService } from '../../../../api/journodex/journodex.api.service';
import { AppearCondition } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { EntityType } from './../../../../api/targeting/enum/entity.type.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';

@Component({
    selector: 'agpr-research-request-dialog',
    templateUrl: 'research-request-dialog.component.html',
    styleUrls: ['research-request-dialog.component.scss'],
    standalone: false
})
export class ResearchRequestDialogComponent extends AbstractDialogComponent implements OnInit {

    @Input() contact: ContactModel;
    @Input() outlet: OutletModel;

    contacts: ContactModel[] = [];
    outlets: OutletModel[] = [];
    comment: string = '';
    noCommentsError: boolean = false;

    disableContact: boolean = false;
    disableOutlet: boolean = false;

    labelLookupField: string = 'name';
    addOnKeys: ActionKeys[] = [ActionKeys.ENTER];

    readonly id = 'researchRequestDialog';
    readonly styleEnum = AgprComponentStyleEnum;
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly entityType = EntityType;

    readonly contactAutocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.contactSearcher.bind(this),
        delay: 300,
        minLength: 3,
        forceSelection: false,
        clearOnBlur: true,
        customOptions: [
            {
                label: 'Core.researchRequest.request.newContact',
                action: (contactName: string) => this.addNewEntity(contactName, EntityType.CONTACT),
                appearCondition: AppearCondition.always,
                clearOnSelect: true
            },
        ],
    };

    readonly outletAutocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.outletSearcher.bind(this),
        delay: 300,
        minLength: 3,
        forceSelection: false,
        clearOnBlur: true,
        customOptions: [
            {
                label: 'Core.researchRequest.request.newOutlet',
                action: (outletName: string) => this.addNewEntity(outletName, EntityType.OUTLET),
                appearCondition: AppearCondition.always,
                clearOnSelect: true
            },
        ],
    };

    constructor(
        public coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private journodexApiService: JournodexApiService,
        private journodexEntityFactory: JournodexEntityFactory,
    ) {
        super(coreDialogService, 'ResearchRequest');
    }

    ngOnInit() {
        if (this.contact) {
            this.contact.name = `${this.contact.firstName} ${this.contact.lastName}`;
            this.contacts = [this.contact];
            this.disableContact = true;
        }

        if (this.outlet) {
            this.outlets = [this.outlet];
            this.disableOutlet = true;
        }
    }

    submit() {
        if (!this.comment) {
            this.noCommentsError = true;

            return;
        }

        const changeRequest: EntityChangeRequestApiModel = this.generateChangeRequest();

        this.journodexApiService.entityChangeRequest(changeRequest).subscribe({
            next: (res) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Core.researchRequest.request.submitted.summary'),
                    detail: this.localizationService.transform('Core.researchRequest.request.submitted.detail')
                });
            },
            error: (error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Core.researchRequest.request.error.summary')
                });
            }
        });
        this.closeDialog();
    }

    cancel() {
        this.closeDialog();
    }

    onCommentChange() {
        this.noCommentsError = this.comment.trim().length === 0;
    }

    addNewEntity(name: string, entityType: EntityType) {
        if (name?.length < 3) {

            return;
        }

        if (entityType === EntityType.CONTACT) {
            const newContact: ContactModel = this.journodexEntityFactory.createEmptyContact();
            newContact.id = null;
            newContact.name = name;
            this.contacts.push(newContact);
        } else {
            const newOutlet: OutletModel = this.journodexEntityFactory.createEmptyOutlet();
            newOutlet.id = null;
            newOutlet.name = name;
            this.outlets.push(newOutlet);
        }
    }

    private outletSearcher(query: string): Observable<OutletModel[]> {
        return this.journodexApiService.performQuickSearchOutlets(query)
            .pipe(map(results => {
                return results.map(result => {
                    const model: OutletModel = this.journodexEntityFactory.createEmptyOutlet();
                    model.id = result.id;
                    model.name = result.entityName;
                    return model;
                });
            }));
    }

    private contactSearcher(query: string): Observable<ContactModel[]> {
        return this.journodexApiService.performQuickSearchContacts(query)
            .pipe(map(results => {
                return results.map(result => {
                    const model: ContactModel = this.journodexEntityFactory.createEmptyContact();
                    model.id = result.id;
                    model.name = result.entityName;
                    return model;
                });
            }));
    }

    private generateChangeRequest(): EntityChangeRequestApiModel {
        if (this.contacts.length > 0) {
            const contact = this.contacts[0];
            const contactName = (contact.firstName && contact.lastName) ?
                `${contact.firstName} ${contact.lastName}` : contact?.name;

            return {
                entityType: EntityChangeRequestTypeEnum.contact,
                entityName: contactName,
                entityId: contact.id,
                note: this.comment.trim()
            };
        } else if (this.outlets.length > 0) {
            const outlet = this.outlets[0];
            return {
                entityType: EntityChangeRequestTypeEnum.outlet,
                entityName: outlet.name,
                entityId: outlet.id,
                note: this.comment.trim()
            };
        } else {
            return {
                entityType: null,
                entityName: null,
                entityId: null,
                note: this.comment.trim()
            };
        }
    }
}
